import { range } from "es-toolkit/math";
import { motion, useAnimate } from "motion/react";
import { useEffect, useRef } from "react";
import seedrandom from "seedrandom";
import { ConstellationBackgroundLayerItemImage } from "./ConstellationBackgroundLayerItemImage";
import { CONSTELLATION_BACKGROUND_ROOT_ID, useConstellationBackgroundStore } from "./ConstellationBackgroundStore";
import { LOOP_DURATION, TRANSITION_DURATION, useConstellationBackgroundConstants } from "./hooks";
const itemImageLayers = 2;
function calculateDelay({
  duration,
  index,
  count,
  random
}) {
  return -(duration * (index / count)) - random * duration;
}
export function ConstellationBackgroundLayerItem({
  item,
  background,
  index,
  blur = true,
  scaleOffset = 0,
  ellipseRotation = 0
}) {
  const constellationBackgroundStore = useConstellationBackgroundStore();
  const animationsRef = useRef([]);
  const backgroundStoreSubscriptionRef = useRef(undefined);

  /**
   * We use a seed here (backgroundId) and not something unique to the item (Math.random)
   * so each item in a background has the same seed which means that none of them will overlap with each other
   */
  const random = seedrandom(background.id)();

  // Figure out duration and delay (e.g. start point) of animation for the item
  const delayOptions = {
    index,
    count: background.items.length,
    random
  };
  const delay = calculateDelay({
    ...delayOptions,
    duration: LOOP_DURATION
  });
  const [scope, animate] = useAnimate();
  const {
    initialState,
    loopAnimation,
    loopAnimationOptions,
    wiggleAnimation,
    wiggleAnimationOptions,
    transitionAnimationOptions
  } = useConstellationBackgroundConstants({
    blur,
    scaleOffset,
    index,
    delay
  });

  // Set up objects which define animation points (e.g. start, running, stop)
  function playAnimations(animations) {
    for (const animation of animations) {
      try {
        if (!animation) {
          continue;
        }
        if (animation.tag) {
          const existingAnimationIndex = animationsRef.current.findIndex(existing => existing.tag === animation.tag);
          const existingAnimation = existingAnimationIndex > -1 && animationsRef.current.at(existingAnimationIndex);
          if (existingAnimation) {
            animation.animation.time = existingAnimation.animation.time + (existingAnimation.options?.delay || delay);
            existingAnimation.animation.stop();
            animationsRef.current.splice(existingAnimationIndex, 1);
          }
          animationsRef.current.push(animation);
        }
        animation.animation.play();
      } catch {
        // motion seem to have a pass where references are not set up for animation.animation
      }
    }
  }
  function createAnimation(to, options, tag) {
    return {
      tag,
      options,
      animation: animate(scope.current, to, {
        ...options,
        autoplay: false
      })
    };
  }
  function show(animationOptions = {}, options_0) {
    const loopSpeedMultiplier = options_0?.loopSpeedMultiplier || 1;
    const duration = (loopAnimationOptions.duration || 0) / loopSpeedMultiplier;
    playAnimations([createAnimation({
      opacity: 1,
      offsetPath: "ellipse(40% 40%)"
    }, {
      ...transitionAnimationOptions,
      ...animationOptions
    }), createAnimation(loopAnimation, {
      ...loopAnimationOptions,
      duration,
      delay: calculateDelay({
        ...delayOptions,
        duration
      })
    }, "default"), createAnimation(wiggleAnimation, wiggleAnimationOptions, "wiggle")]);
  }
  function hide(animationOptions_0 = {}, options_1) {
    const defocus = Boolean(options_1?.defocusZoom);
    playAnimations([createAnimation({
      ...initialState,
      ...(background.id !== CONSTELLATION_BACKGROUND_ROOT_ID && {
        offsetDistance: undefined
      }),
      ...(defocus && {
        scale: undefined,
        filter: undefined,
        offsetPath: "ellipse(35% 35%)",
        offsetDistance: undefined,
        ...(blur && {
          opacity: undefined
        })
      })
    }, {
      ...transitionAnimationOptions,
      ease: defocus ? "circInOut" : "easeIn",
      ...animationOptions_0
    }),
    // Pause wiggle animation if entire animation is being hidden
    !defocus && createAnimation({}, loopAnimationOptions, "wiggle"), defocus && createAnimation({
      ...loopAnimation,
      filter: undefined
    }, loopAnimationOptions, "default"), defocus && blur && createAnimation({
      filter: "blur(20px)",
      opacity: 0.25,
      offsetPath: options_1?.defocusZoom === "out" ? "ellipse(50% 50%)" : "ellipse(35% 35%)"
    }, {
      ...transitionAnimationOptions,
      delay: (transitionAnimationOptions.delay || 0) / 3 - 0.75
    })]);
    if (!defocus) {
      animationsRef.current.forEach(animation_0 => {
        animation_0.animation.stop();
      });
      animationsRef.current = [];
    }
  }
  function runPresentationMethod(method) {
    const currentBackground = constellationBackgroundStore.getState().backgrounds.find(bg => bg.id === background.id);
    switch (method) {
      case "show":
        show({
          duration: TRANSITION_DURATION,
          delay: index / 75 - TRANSITION_DURATION / 2
        }, currentBackground?.options);
        break;
      case "hide":
        hide({
          duration: TRANSITION_DURATION / 1.5,
          delay: 0
        }, currentBackground?.options);
        break;
      case "defocus":
        hide({
          duration: TRANSITION_DURATION / 3,
          delay: 0,
          ease: "circOut"
        }, currentBackground?.options);
        break;
    }
  }
  function unsubscribe() {
    for (const ref of [backgroundStoreSubscriptionRef]) {
      if (!ref.current) {
        continue;
      }
      ref.current();
    }
  }
  function subscribe() {
    // If the item is in the visible layer, then show
    const {
      open,
      getTopLevelBackgroundId
    } = constellationBackgroundStore.getState();
    if (open && getTopLevelBackgroundId() === background.id) {
      runPresentationMethod("show");
    }

    // Make sure we're not subscribing twice
    unsubscribe();
    const isRootBackground = CONSTELLATION_BACKGROUND_ROOT_ID === background.id;
    backgroundStoreSubscriptionRef.current = constellationBackgroundStore.subscribe((state, prevState) => {
      const currentBackground_0 = state.backgrounds.find(bg_0 => bg_0.id === background.id);
      const currentTopLevelBackgroundId = getTopLevelBackgroundId();
      const previousTopLevelBackgroundId = getTopLevelBackgroundId(prevState.backgrounds);
      if (!prevState.open && state.open && isRootBackground) {
        runPresentationMethod("show");
      }
      if (prevState.open && !state.open) {
        runPresentationMethod("hide");
      }

      // New background pushed
      if (previousTopLevelBackgroundId) {
        // If the background has been popped, blur / hide
        if (currentTopLevelBackgroundId !== background.id && previousTopLevelBackgroundId === background.id) {
          // Defocus (still show but make less prominant) if it's the root trending bg
          // If not, then fully hide
          runPresentationMethod(currentBackground_0?.options?.defocusZoom ? "defocus" : "hide");
        }

        // If the background has just been made visible, show item
        if (currentTopLevelBackgroundId === background.id && previousTopLevelBackgroundId !== background.id) {
          runPresentationMethod("show");
        }
        return;
      }

      // Current background visibility updated
      const previousBackground = prevState.backgrounds.find(bg_1 => bg_1.id === background.id);
      if (currentBackground_0?.visible && !previousBackground?.visible) {
        runPresentationMethod("show");
      }
      if (!currentBackground_0?.visible && previousBackground?.visible) {
        runPresentationMethod(currentBackground_0?.options?.defocusZoom ? "defocus" : "hide");
      }
    });
  }
  useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, []);
  if (!item.imageUrl) {
    return;
  }
  return (
    // Ellipse Layer
    <motion.div className="fixed size-16" initial={initialState} key={item.id} ref={scope} style={{
      offsetPath: "ellipse(40% 40%)",
      offsetRotate: `${ellipseRotation.toString()}deg`
    }} transition={loopAnimationOptions}>
      {range(itemImageLayers).map(i => <ConstellationBackgroundLayerItemImage blur={i === 1} imageUrl={item.imageUrl} key={i} onSubscribe={subscribe} subscribe={i === 0} />)}
    </motion.div>
  );
}